import { observer }              from 'mobx-react-lite';
import ListCollection            from '@widesk-ui/components/listCollection/ListCollection';
import View                      from '@widesk-ui/components/View';
import Tag                       from '@widesk-ui/components/Tag';
import { EyeOutlined }           from '@ant-design/icons';
import { HistoryOutlined }       from '@ant-design/icons';
import ButtonActions             from '@widesk-ui/components/ButtonActions';
import ProjectVersionModel       from '@models/map/ProjectVersionModel';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { message }               from '@widesk-ui/hooks/useMessage';
import { openPreview }           from '@/TokamapEditor/ux/Header/PreviewModal';
import restoreProjectVersion     from '@/TokamapEditor/helpers/restoreProjectVersion';
import CompactControls from '@/TokamapEditor/ux/CompactControls';

/**
 * L'historique des publications du PROJET : une ligne par publication, et non plus par version de
 * carte. C'est l'échelle à laquelle on publie désormais — une demande met à jour toutes les cartes
 * du projet, puis les regroupe dans un fichier condensé.
 *
 * Une publication apparaît dès qu'elle est demandée, AVANT d'être faite : le `POST` rend la main
 * tout de suite et un consumer travaille ensuite. Tant que `fileUrl` est vide, rien n'est en ligne,
 * d'où l'état « en cours » et le fait que « En ligne » désigne la dernière publication ACHEVÉE, pas
 * la plus récente.
 *
 * Rien n'est dit des cartes : une publication les couvre toutes, le backoffice ne propose pas d'en
 * publier une partie (voir `ProjectVersionPublishForm`).
 */
const HistoryListSidebar = observer(() => {
	const tokens = useDesignTokens();
	const projectStore = currentProjectEditorStore.get;

	return (
		<CompactControls>
			<ListCollection
				modelClass={ProjectVersionModel}
				onDidMount={listStore => {
					if (!leftSidebarStore.getListStore('history')) {
						leftSidebarStore.setListStore('history', listStore);
					}
				}}
				onFetchCollectionStart={async coll => {
					coll.setRequiredFilter('project', projectStore.project.id);
				}}
				defaultSortName="number"
				defaultSortWay="desc"
				pagination={{ pageSize: 20 }}
				itemProps={(version, listStore) => {
					const isPending = !version.isPublished;
					// Ce qui est EN LIGNE, donc la dernière publication achevée : une publication en cours ne
					// remplace rien tant que son fichier condensé n'existe pas.
					const isCurrent = !isPending
						&& (listStore.data as ProjectVersionModel[]).find(v => v.isPublished) === version;

					return {
						extraProps: { style: { position: 'relative' }, paddingV: 'sm', paddingH: 'md' },
						renderLeft: () => (
							<>
								<div style={{ width: 14 }} />

								<div
									style={{
										position: 'absolute',
										left: 15,
										top: 0,
										bottom: 0,
										width: 2,
										background: tokens.border.subtle,
									}}
								/>

								<div
									style={{
										position: 'absolute',
										left: 10,
										top: 15,
										width: 12,
										height: 12,
										borderRadius: '50%',
										border: `2px solid ${isCurrent ? tokens.color.primary : tokens.border.default}`,
										background: isCurrent ? tokens.color.primary : tokens.surface.card,
										boxShadow: isCurrent ? `0 0 0 3px ${tokens.color.primary}26` : undefined,
									}}
								/>
							</>
						),
						// Les actions sont rendues à la main plutôt que via `actions` : `ButtonActions` bascule en
						// bouton simple quand il n'en reste qu'une de visible, or l'historique garde les trois
						// petits points sur toutes les lignes, quel que soit le nombre d'actions restantes.
						renderRight: () => (
							<ButtonActions
								forceDropdown
								size="small"
								stopPropagation
								actions={[
									{
										// Le PROJET tel qu'il était en ligne à cette publication, pas seulement ce qu'elle a
										// changé : son fichier condensé porte toutes les cartes du projet. Rien à montrer
										// tant qu'il n'est pas assemblé.
										hidden: isPending,
										icon: <EyeOutlined />,
										label: 'Aperçu',
										onClick: () => openPreview(version),
									},
									{
										// Proposée aussi sur la publication en ligne : la charger, c'est défaire les
										// modifications faites depuis. Une publication inachevée, elle, n'a pas de fichier.
										hidden: isPending,
										icon: <HistoryOutlined />,
										label: 'Charger dans l\'éditeur',
										confirm: {
											title: `Charger la publication ${version.displayNumber} dans l'éditeur ?`,
											// Tout est dit ici : l'éditeur se recharge ensuite, un message final ne serait pas lu.
											content: (
												<View gap="sm">
													<View>
														Les formes de toutes les cartes du projet seront remplacées par celles de
														cette publication, et chaque carte retrouvera le cadrage qu'elle avait alors.
													</View>

													<View>
														Les points d'intérêt supprimés depuis ne seront pas rétablis : les formes qui
														les ouvraient reviendront sans action.
													</View>

													<View bold>Cette action ne peut pas être annulée.</View>
												</View>
											),
											okText: 'Charger',
											okType: 'danger',
										},
										onClick: async () => {
											try {
												const report = await restoreProjectVersion(version);

												console.log(`[restore] publication ${version.displayNumber}`, report);

												// Pas de rechargement : ce message partirait avec la page.
												if (report.configurationsFailed) {
													message.open({
														type: 'warning',
														duration: 0,
														content: `Formes chargées, mais le cadrage de ${report.configurationsFailed} carte(s) n'a pas pu l'être. Rechargez la page pour voir les formes.`,
													});

													return;
												}

												// La correspondance forme -> feature que l'éditeur tient en mémoire ne désigne
												// plus rien : continuer à dessiner par-dessus enregistrerait dans le vide.
												window.location.reload();
											} catch (err) {
												message.exception(err);
											}
										},
									},
								]}
							/>
						),
						children: (
							<View gap={2}>
								<View row gap={8}>
									<View bold size={13}>Publication {version.displayNumber}</View>

									{isCurrent && (
										<Tag color={tokens.color.primary}>
											En ligne
										</Tag>
									)}

									{isPending && (
										<Tag color={tokens.color.warning}>
											Publication en cours
										</Tag>
									)}
								</View>

								<View size={12} color={tokens.text.muted}>
									Publiée le {version.createdAtFormatted} par {version.createdBy.fullName}
								</View>

								{!!version.comment && (
									<View size={12} style={{ whiteSpace: 'pre-wrap' }}>
										{version.comment}
									</View>
								)}
							</View>
						),
					};
				}}
			/>
		</CompactControls>
	);
});

export default HistoryListSidebar;
